import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './Payments.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const formatFullNumber = (num) => {
  if (num === null || num === undefined) return '0';
  if (typeof num !== 'number') num = parseFloat(num);
  if (isNaN(num)) return '0';
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const Payments = () => {
  const { token } = useAuth();
  const socket = useSocket();
  const [payments, setPayments] = useState([]);
  const [walletBalances, setWalletBalances] = useState({ USD: 0, EUR: 0, GBP: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState('USD');
  const [withdrawMethod, setWithdrawMethod] = useState('bank');
  const [bankDetails, setBankDetails] = useState({
    bankAccountName: '',
    bankAccountNumber: '',
    bankName: '',
    bankIban: '',
    bankSwift: ''
  });
  const [bankDetailsExist, setBankDetailsExist] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    fetchData();
    fetchBankDetails();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('payment_sent', (data) => {
        alert(`Payment of ${data.requestedCurrency} ${data.requestedAmount?.toLocaleString()} has been sent.`);
        fetchData();
      });
    }
    return () => {
      if (socket) socket.off('payment_sent');
    };
  }, [socket]);

  const fetchBankDetails = async () => {
    try {
      const res = await fetch(`${API_BASE}/wallet/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.bankDetails) {
        const details = data.bankDetails;
        setBankDetails({
          bankAccountName: details.bankAccountName || '',
          bankAccountNumber: details.bankAccountNumber || '',
          bankName: details.bankName || '',
          bankIban: details.bankIban || '',
          bankSwift: details.bankSwift || ''
        });
        const hasDetails = !!(details.bankAccountName && details.bankAccountNumber && details.bankName);
        setBankDetailsExist(hasDetails);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const saveBankDetails = async () => {
    if (!bankDetails.bankAccountName || !bankDetails.bankAccountNumber || !bankDetails.bankName) {
      alert('Please fill at least Account Name, Account Number, and Bank Name');
      return;
    }
    setSavingBank(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/bank-details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(bankDetails)
      });
      const data = await res.json();
      if (data.success) {
        alert('Bank details saved successfully');
        setBankDetailsExist(true);
        setShowBankModal(false);
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error saving bank details');
    } finally {
      setSavingBank(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, walletRes, contractsRes] = await Promise.all([
        fetch(`${API_BASE}/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/wallet`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/contracts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const paymentsData = await paymentsRes.json();
      const walletData = await walletRes.json();
      const contractsData = await contractsRes.json();

      let contractsMap = {};
      if (contractsData.success && contractsData.contracts) {
        contractsMap = contractsData.contracts.reduce((map, contract) => {
          map[contract._id] = contract;
          return map;
        }, {});
      }

      let paymentList = [];
      if (paymentsData.success && paymentsData.payments) {
        paymentList = paymentsData.payments.map(p => {
          const contract = contractsMap[p.contractId];
          let grossAmount = p.amount || p.totalAmount || 0;
          if (grossAmount === 0 && contract) {
            grossAmount = contract.totalAmount || 0;
          }
          const platformFee = grossAmount * 0.10;
          const netAmount = grossAmount - platformFee;

          return {
            _id: p._id,
            vesselName: p.vesselName || contract?.vesselName || 'N/A',
            serviceType: p.serviceType || contract?.serviceType || 'N/A',
            amount: netAmount,
            totalOwnerPaid: grossAmount,
            platformFee: platformFee,
            status: p.status || 'pending',
            createdAt: p.createdAt || p.paymentDate || new Date(),
            releasedDate: p.releasedDate || (p.status === 'released' ? new Date() : null),
            contractId: p.contractId,
            currency: contract?.currency || 'USD'
          };
        });
      } else if (contractsData.success && contractsData.contracts) {
        paymentList = contractsData.contracts
          .filter(c => c.status === 'payment_released' || c.status === 'work_completed')
          .map(contract => {
            const grossAmount = contract.totalAmount || 0;
            const platformFee = grossAmount * 0.10;
            const netAmount = grossAmount - platformFee;
            return {
              _id: contract._id,
              vesselName: contract.vesselName || 'N/A',
              serviceType: contract.serviceType || 'N/A',
              amount: netAmount,
              totalOwnerPaid: grossAmount,
              platformFee: platformFee,
              status: contract.status === 'payment_released' ? 'released' : 'pending',
              createdAt: contract.createdAt,
              releasedDate: contract.updatedAt,
              contractId: contract._id,
              currency: contract.currency || 'USD'
            };
          });
      }

      setPayments(paymentList);

      if (walletData.success) {
        setWalletBalances(walletData.balances || { USD: 0, EUR: 0, GBP: 0 });
        setTransactions(walletData.transactions || []);
      } else {
        setWalletBalances({ USD: 0, EUR: 0, GBP: 0 });
        setTransactions([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setPayments([]);
      setWalletBalances({ USD: 0, EUR: 0, GBP: 0 });
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) return alert('Enter valid amount');
    if (amount > walletBalances[withdrawCurrency]) return alert(`Insufficient ${withdrawCurrency} balance`);

    if (!bankDetailsExist) {
      alert('Please add your bank details before requesting a withdrawal.');
      setShowBankModal(true);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount, method: withdrawMethod, currency: withdrawCurrency })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Withdrawal request submitted for ${amount} ${withdrawCurrency}.`);
        setShowWithdrawModal(false);
        fetchData();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error submitting withdrawal');
    }
  };

  if (loading) return <div className="loading-spinner">Loading payments...</div>;

  const getCurrencySymbol = (code) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£' };
    return symbols[code] || code || '';
  };

  // Calculate per-currency totals
  const totalsByCurrency = payments.reduce((acc, p) => {
    const cur = p.currency || 'USD';
    if (!acc[cur]) {
      acc[cur] = { totalEarned: 0, platformFee: 0, pending: 0 };
    }
    acc[cur].totalEarned += p.amount || 0;
    acc[cur].platformFee += p.platformFee || 0;
    if (p.status !== 'released') {
      acc[cur].pending += p.amount || 0;
    }
    return acc;
  }, {});

  // Ensure all three currencies exist even if zero
  const currencies = ['USD', 'EUR', 'GBP'];
  currencies.forEach(cur => {
    if (!totalsByCurrency[cur]) {
      totalsByCurrency[cur] = { totalEarned: 0, platformFee: 0, pending: 0 };
    }
  });

  const getStatusClass = (status) => {
    return status === 'released' ? 'released' : 'pending';
  };

  const getStatusLabel = (status) => {
    return status === 'released' ? 'Released' : 'Pending';
  };

  const openDetailsModal = (payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
  };

  return (
    <div className="payments-wrapper">
      <div className="payments-header">
        <div className="payments-header-text">
          <h1><i className="fas fa-wallet"></i> Payments</h1>
          <p>Track your earnings and request withdrawals</p>
        </div>
      </div>

      <div className="payments-balance-grid">
        <div className="balance-card" onClick={() => setShowBankModal(true)}>
          <div className="balance-icon"><i className="fas fa-dollar-sign"></i></div>
          <div className="balance-amount">{formatFullNumber(walletBalances.USD)}</div>
          <div className="balance-label">USD Balance</div>
        </div>
        <div className="balance-card" onClick={() => setShowBankModal(true)}>
          <div className="balance-icon"><i className="fas fa-euro-sign"></i></div>
          <div className="balance-amount">{formatFullNumber(walletBalances.EUR)}</div>
          <div className="balance-label">EUR Balance</div>
        </div>
        <div className="balance-card" onClick={() => setShowBankModal(true)}>
          <div className="balance-icon"><i className="fas fa-pound-sign"></i></div>
          <div className="balance-amount">{formatFullNumber(walletBalances.GBP)}</div>
          <div className="balance-label">GBP Balance</div>
        </div>
      </div>

      <div className="payments-stats-grid">
        {currencies.map(cur => (
          <div key={cur} className="stat-card-currency">
            <div className="stat-currency-header">
              <span className="currency-label">{cur}</span>
              <span className="currency-symbol">{getCurrencySymbol(cur)}</span>
            </div>
            <div className="stat-currency-row">
              <span className="stat-label">Total Earned</span>
              <span className="stat-value">{formatFullNumber(totalsByCurrency[cur].totalEarned)}</span>
            </div>
            <div className="stat-currency-row">
              <span className="stat-label">Platform Fees (10%)</span>
              <span className="stat-value">{formatFullNumber(totalsByCurrency[cur].platformFee)}</span>
            </div>
            <div className="stat-currency-row">
              <span className="stat-label">Pending Payment</span>
              <span className="stat-value">{formatFullNumber(totalsByCurrency[cur].pending)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="payments-actions-row">
        <Button variant="primary" onClick={() => setShowWithdrawModal(true)} icon="fa-money-bill-wave">
          Withdraw Funds
        </Button>
        <Button variant="secondary" onClick={() => setShowBankModal(true)} icon="fa-university">
          Bank Details
        </Button>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Vessel</th>
              <th>Service</th>
              <th>Amount (Net)</th>
              <th>Platform Fee (10%)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr><td colSpan="6" className="empty-row">No payments found</td></tr>
            ) : (
              payments.map(p => (
                <tr key={p._id}>
                  <td>{p.vesselName}</td>
                  <td>{p.serviceType}</td>
                  <td className="payment-amount">{getCurrencySymbol(p.currency)}{formatFullNumber(p.amount || 0)} {p.currency}</td>
                  <td className="payment-fee">{getCurrencySymbol(p.currency)}{formatFullNumber(p.platformFee || 0)} {p.currency}</td>
                  <td><span className={`payment-status ${getStatusClass(p.status)}`}>{getStatusLabel(p.status)}</span></td>
                  <td className="actions-cell">
                    <button className="action-icon" onClick={() => openDetailsModal(p)} title="View Details">
                      <i className="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="transaction-history-section">
        <h3><i className="fas fa-history"></i> Transaction History</h3>
        <div className="transaction-list">
          {transactions.length === 0 ? (
            <div className="empty-transactions">No transactions yet</div>
          ) : (
            transactions.map(tx => (
              <div key={tx._id} className="transaction-card">
                <div className="transaction-date">{new Date(tx.createdAt).toLocaleDateString()}</div>
                <div className="transaction-details">
                  <div className="tx-title">{tx.description || 'Transaction'}</div>
                  <div className="tx-meta">
                    <span className="tx-ref">ID: {tx._id.slice(-8)}</span>
                    <span className="tx-ref">Currency: {tx.currency || 'USD'}</span>
                  </div>
                </div>
                <div className={`transaction-amount ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                  {tx.amount > 0 ? '+' : ''}{formatFullNumber(tx.amount || 0)} {tx.currency || 'USD'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} title="Withdraw Funds">
        <div className="withdraw-form">
          <div className="form-group">
            <label>Currency</label>
            <select value={withdrawCurrency} onChange={(e) => setWithdrawCurrency(e.target.value)}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
            <small>Available balance: {formatFullNumber(walletBalances[withdrawCurrency])} {withdrawCurrency}</small>
          </div>
          <div className="form-group">
            <label>Amount</label>
            <input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="Enter amount" />
          </div>
          <div className="form-group">
            <label>Method</label>
            <select value={withdrawMethod} onChange={(e) => setWithdrawMethod(e.target.value)}>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>
          {!bankDetailsExist && (
            <div className="warning-message" style={{ backgroundColor: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '8px', color: '#ef4444' }}>
              <i className="fas fa-exclamation-triangle"></i> Please add your bank details before withdrawing.
            </div>
          )}
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowWithdrawModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleWithdraw} disabled={!bankDetailsExist}>Request Withdrawal</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showBankModal} onClose={() => setShowBankModal(false)} title="Bank Account Details">
        <div className="bank-details-form">
          <div className="form-group">
            <label>Account Holder Name *</label>
            <input type="text" value={bankDetails.bankAccountName} onChange={(e) => setBankDetails({...bankDetails, bankAccountName: e.target.value})} placeholder="Full name as on bank account" />
          </div>
          <div className="form-group">
            <label>Account Number *</label>
            <input type="text" value={bankDetails.bankAccountNumber} onChange={(e) => setBankDetails({...bankDetails, bankAccountNumber: e.target.value})} placeholder="Bank account number" />
          </div>
          <div className="form-group">
            <label>Bank Name *</label>
            <input type="text" value={bankDetails.bankName} onChange={(e) => setBankDetails({...bankDetails, bankName: e.target.value})} placeholder="Name of the bank" />
          </div>
          <div className="form-group">
            <label>IBAN (Optional)</label>
            <input type="text" value={bankDetails.bankIban} onChange={(e) => setBankDetails({...bankDetails, bankIban: e.target.value})} placeholder="International Bank Account Number" />
          </div>
          <div className="form-group">
            <label>SWIFT/BIC (Optional)</label>
            <input type="text" value={bankDetails.bankSwift} onChange={(e) => setBankDetails({...bankDetails, bankSwift: e.target.value})} placeholder="SWIFT code for international transfers" />
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowBankModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={saveBankDetails} disabled={savingBank}>
              {savingBank ? 'Saving...' : 'Save Details'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Payment Details">
        {selectedPayment && (
          <div className="payment-details-modal">
            <div className="detail-row">
              <span className="detail-label">Vessel:</span>
              <span className="detail-value">{selectedPayment.vesselName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Service:</span>
              <span className="detail-value">{selectedPayment.serviceType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Payment Date:</span>
              <span className="detail-value">{new Date(selectedPayment.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Platform Fee (10%):</span>
              <span className="detail-value">{getCurrencySymbol(selectedPayment.currency)}{formatFullNumber(selectedPayment.platformFee || 0)} {selectedPayment.currency}</span>
            </div>
            <div className="detail-row highlight">
              <span className="detail-label">Your Net Amount:</span>
              <span className="detail-value">{getCurrencySymbol(selectedPayment.currency)}{formatFullNumber(selectedPayment.amount || 0)} {selectedPayment.currency}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`payment-status ${getStatusClass(selectedPayment.status)}`}>{getStatusLabel(selectedPayment.status)}</span>
            </div>
            {selectedPayment.releasedDate && (
              <div className="detail-row">
                <span className="detail-label">Released Date:</span>
                <span className="detail-value">{new Date(selectedPayment.releasedDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payments;